import React, { useCallback, useState } from "react";
import { Typography, Box, Grid, Button } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { makeStyles } from "@mui/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CandidateCardNew from "bundles/common/components/CandidateCardNew.jsx";
import sh from "bundles/common/utils/sh";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import CandidateDialog from "bundles/common/components/CandidateDialog.jsx";

const SuggestedCandidates = ({ candidates, loading }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [rejectedCandidates, setRejectedCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isCandidateDialogOpen, setIsCandidateDialogOpen] = useState(false);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));

  
  const useStyles = makeStyles(() => ({
    cardContainer: {
      padding: "20px",
      borderRadius: "12px",
      boxShadow: "none",
      border: "1px solid #000",
      position: "relative",
      overflow: isDesktop ? "visible" : "hidden",
    },
    candidateStatusButton: {
      overflow: "hidden", 
      textOverflow: "ellipsis", 
      whiteSpace: "nowrap",
      textTransform: "capitalize",
      borderRadius: "999px",
      color: "#000 !important",
      padding: "4px 6px",
      height: "30px",
    },
    seeMoreButton: {
      textTransform: "none", 
      fontWeight: "bold", 
      fontFamily: "Segoe UI Bold, sans-serif",
      padding: "12px 100px", 
      border: "1px solid #000", 
      borderRadius: "999px", 
      width: isDesktop ? "35%" : "100%"
    }
  }));
  
  const classes = useStyles();
  
  const updateCurrentRecruiter = useCallback(
    (recruiter) => {
      dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: recruiter });
    },
    [dispatch]
  );

  const handleSuggestionAction = useCallback((candidateId, action = "toggle") => {
    // Determine the status based on action
    let newStatus;
    
    if (action === "reject") {
      newStatus = "rejected";
    } else if (action === "toggle") {
      newStatus = "saved";
    }

    sh.put("/recruiter/favorite_candidate", {
      authenticity_token: authenticityToken,
      candidate_id: candidateId,
      status: newStatus
    }).then((res) => {
      if (res.data) {
        if (action === "toggle") {
          setFavorites([...favorites, candidateId]);
        }
        
        let message;
        if (action === "reject") {
          message = intl.formatMessage({ id: "recruiter.candidate_rejected" });
        } else {
          message = intl.formatMessage({ id: "recruiter.added_to_favorites" });
        }
        
        enqueueSnackbar(message, { variant: "success" });
        updateCurrentRecruiter(res.data);
        setRejectedCandidates([...rejectedCandidates, candidateId]);
      }
    }).catch((err) => {
      console.error("Error updating suggestion status:", err);
      enqueueSnackbar(
        intl.formatMessage({ id: "recruiter.favorite_update_failed" }),
        { variant: "error" }
      );
    });
  }, [favorites, rejectedCandidates, authenticityToken, intl, updateCurrentRecruiter, enqueueSnackbar]);

  const handleFavoriteToggle = useCallback((candidateId, action) => {
    handleSuggestionAction(candidateId, action);
  }, [handleSuggestionAction]);
  
  const visibleCandidates = candidates?.filter(
    candidate => !rejectedCandidates.includes(candidate?.id)
  );

  const handleOpenCandidate = useCallback((candidate) => {
    if (!candidate) return;
    setSelectedCandidate(candidate);
    setIsCandidateDialogOpen(true);
  }, []);

  return(
    <>
      <Box>
        <Box sx={{mt: 4}}>
          <Typography variant={isDesktop ? "h5" : "subtitle1"} sx={{textAlign: "center", fontWeight: "bold", fontFamily: "Segoe UI Bold, sans-serif", fontSize: "20px !important" }}>
            <FormattedMessage id="recruiter.suggested_candidates" />
          </Typography>
        </Box>

        {!loading && visibleCandidates.length > 0 && (
          <>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {visibleCandidates
                  .slice(0, 4)
                  .map((candidate) => (
                    <Grid item xs={12} md={6} key={candidate?.id} sx={{ display: "flex" }}>
                      <CandidateCardNew
                        candidate={candidate}
                        handleFavoriteToggle={handleFavoriteToggle}
                        favorites={favorites}
                        showCloseIcon={true}
                        chipVariant="availability"
                        onCardClick={handleOpenCandidate}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
            {visibleCandidates.length > 4 && (
              <Box sx={{ mt: 3, alignItems: "center", display: "flex", justifyContent: "center" }}>
                <Button variant="text" className={classes.seeMoreButton} onClick={() => { navigate(`/${i18nLocale}/recruiter/my_candidates`);}}>
                  <FormattedMessage id="recruiter.view_more" />
                </Button>
              </Box>
            )}
          </>
        )}

        {!loading && visibleCandidates.length === 0 && (
          <Box sx={{ mt: 4, textAlign: "center", py: 4 }}>
            <Typography variant={isDesktop ? "h6" : "body1"}>
              <FormattedMessage id="recruiter.no_suggested_candidates" />
            </Typography>
          </Box>
        )}
      </Box>

      {selectedCandidate && (
        <CandidateDialog
          candidate={selectedCandidate}
          jobApplication={null}
          jobApplications={[]}
          onClose={() => setSelectedCandidate(null)}
          isDialogOpen={isCandidateDialogOpen}
          setIsDialogOpen={setIsCandidateDialogOpen}
          isModal={true}
          columns={[]}
          currentColumnId={null}
        />
      )}
    </>
  );
};

export default SuggestedCandidates;